import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  Check,
  Edit2,
  Eye,
  Globe,
  Loader2,
  Mail,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EditTenantForm } from '../../components/EditTenantForm';
import { PartnerResourceModal } from '../../components/PartnerResourceModal';
import { PartnerUsersTable } from '../../components/PartnerUsersTable';
import { TransactionsTable } from '../../components/TransactionsTable';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/useToast';
import { partnerApi, PartnerLocation, ResourceBySchema } from '../../lib/api';
import { BrandConfigCard } from './BrandConfigCard';
import { TenantLogo } from './TenantLogo';
import { TenantSmsTab } from './TenantSmsTab';

type TabName = 'general' | 'locations' | 'users' | 'transactions' | 'sms';

// Component to display all resources by schema for a tenant
function ResourcesBySchemaCard({ resources }: { resources: ResourceBySchema[] }) {
  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <h4 className="text-lg font-medium text-gray-900 mb-4">Resources</h4>
      {!resources || resources.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          No resources found for this tenant. Resources will appear here once data is synced from your
          system.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schema name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Resources
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Earliest resource created at
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Latest resource created at
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {resources.map(resource => (
                <tr key={resource.schemaDefinitionId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {resource.schemaName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {resource.count.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(resource.earliestCreatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(resource.latestCreatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export const TenantDetail: React.FC = () => {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Initialize activeTab from URL parameter, default to 'general'
  const [activeTab, setActiveTab] = useState<TabName>(() => {
    const validTabs: TabName[] = ['general', 'locations', 'users', 'transactions', 'sms'];
    return tab && validTabs.includes(tab as TabName) ? (tab as TabName) : 'general';
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<PartnerLocation | null>(null);
  const [showLocationResourceModal, setShowLocationResourceModal] = useState(false);
  const [selectedLocationSchemaId, setSelectedLocationSchemaId] = useState<string>('');

  // Handle URL changes (browser back/forward) - only when URL changes
  useEffect(() => {
    const validTabs: TabName[] = ['general', 'locations', 'users', 'transactions', 'sms'];
    const urlTab = tab && validTabs.includes(tab as TabName) ? (tab as TabName) : 'general';

    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [tab, activeTab]);

  // Helper function to handle tab navigation
  const handleTabChange = (newTab: TabName) => {
    const currentPath = `/dashboard/tenants/${id}`;
    const newPath = newTab === 'general' ? currentPath : `${currentPath}/${newTab}`;

    // Update both state and URL
    setActiveTab(newTab);
    navigate(newPath, { replace: true });
  };

  const handleViewLocationResource = async (location: PartnerLocation) => {
    setSelectedLocation(location);
    setShowLocationResourceModal(true);

    // If there are multiple schemas, try to find the one that works
    if (locationSchemas.length > 1) {
      setSelectedLocationSchemaId(''); // Clear selection to trigger the search

      // Try each schema until one succeeds
      for (const schema of locationSchemas) {
        try {
          await partnerApi.getPartnerResourceByExternalId(id!, location.externalId, schema.schemaId);
          // If successful, set this schema and break
          setSelectedLocationSchemaId(schema.schemaId);
          break;
        } catch (error) {
          // Continue to next schema if this one fails
          console.log(`Schema ${schema.schemaId} failed for location ${location.externalId}:`, error);
        }
      }
    } else if (locationSchemas.length === 1) {
      // Single schema, use it directly
      setSelectedLocationSchemaId(locationSchemas[0].schemaId);
    }
  };

  const {
    data: tenant,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => partnerApi.getTenant(id!),
    enabled: !!id,
  });

  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['tenant-locations', id],
    queryFn: () => partnerApi.getTenantLocations(id!),
    enabled: !!id && activeTab === 'locations',
  });

  // Fetch schema mappings to get location schemas
  const { data: schemaMappings } = useQuery({
    queryKey: ['schemaMappings'],
    queryFn: () => partnerApi.getSchemaMappings(),
  });

  // Get location schemas from mappings
  const locationSchemas = schemaMappings?.locationsSchema || [];

  // Fetch tenant resource statistics
  const { data: resourceStats } = useQuery({
    queryKey: ['tenant-resource-stats', id],
    queryFn: () => partnerApi.getTenantResourceStats(id!),
    enabled: !!id && activeTab === 'general',
  });

  /* Fetch SMS registration status separately */
  const { data: smsRegistrationStatus } = useQuery({
    queryKey: ['tenant-sms-registration', id],
    queryFn: () => partnerApi.getTenantSmsRegistrationStatus(id!),
    enabled: !!id,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => partnerApi.deactivateTenant(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      addToast({
        variant: 'success',
        title: 'Tenant deactivated successfully',
      });
      setShowDeactivateDialog(false);
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to deactivate tenant',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => partnerApi.reactivateTenant(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      addToast({
        variant: 'success',
        title: 'Tenant reactivated successfully',
      });
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to reactivate tenant',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const updateUiDefaultsMutation = useMutation({
    mutationFn: (uiDefaults: {
      emailHelpDialogShow?: boolean;
      engageOnboardingShow?: boolean;
      acquireLearningPhaseDisabled?: boolean;
    }) => partnerApi.updateTenant(id!, { uiDefaults }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      addToast({
        variant: 'success',
        title: 'UI preferences updated successfully',
      });
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to update UI preferences',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const handleUiDefaultChange = (
    key: 'emailHelpDialogShow' | 'engageOnboardingShow' | 'acquireLearningPhaseDisabled',
    value: boolean,
  ) => {
    const currentUiDefaults = tenant?.uiDefaults || {};
    const newUiDefaults = {
      ...currentUiDefaults,
      [key]: value,
    };
    updateUiDefaultsMutation.mutate(newUiDefaults);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="bg-red-50 p-4 rounded-lg">
        <h3 className="text-sm font-medium text-red-800">Error loading tenant</h3>
        <div className="mt-2 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Tenant not found'}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/tenants')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tenants
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <TenantLogo branding={tenant.branding} tenantName={tenant.name} size="md" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-medium text-gray-900 font-nav">{tenant.name}</h1>
                {tenant.deletedAt && (
                  <span className="inline-flex items-center rounded-md bg-red-50 px-2.5 py-1 text-sm font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                    Deactivated
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">Tenant ID: {tenant.externalId}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {tenant.deletedAt && (
              <Button
                variant="default"
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}>
                {reactivateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reactivating...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reactivate Tenant
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('general')}
            className={`${
              activeTab === 'general'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}>
            General Information
          </button>
          <button
            onClick={() => handleTabChange('locations')}
            className={`${
              activeTab === 'locations'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}>
            Locations
          </button>
          <button
            onClick={() => handleTabChange('users')}
            className={`${
              activeTab === 'users'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}>
            Users
          </button>
          <button
            onClick={() => handleTabChange('transactions')}
            className={`${
              activeTab === 'transactions'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}>
            Transactions
          </button>
          <button
            onClick={() => handleTabChange('sms')}
            className={`${
              activeTab === 'sms'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}>
            SMS
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
        {activeTab === 'general' && (
          <div className="space-y-6">
            {isEditing ? (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Tenant Information</h3>
                <EditTenantForm
                  tenant={tenant}
                  onCancel={() => setIsEditing(false)}
                  onSuccess={() => setIsEditing(false)}
                />
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tenant Information</h3>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{tenant.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">External ID</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">{tenant.externalId}</dd>
                  </div>
                  {tenant.website && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Website</dt>
                      <dd className="mt-1">
                        <a
                          href={tenant.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:text-primary/80 inline-flex items-center">
                          <Globe className="w-4 h-4 mr-1" />
                          {tenant.website}
                        </a>
                      </dd>
                    </div>
                  )}
                  {tenant.branding?.primaryEmail && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Tenant Email</dt>
                      <dd className="mt-1">
                        <a
                          href={`mailto:${tenant.branding.primaryEmail}`}
                          className="text-sm text-primary hover:text-primary/80 inline-flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {tenant.branding.primaryEmail}
                        </a>
                      </dd>
                    </div>
                  )}
                  {smsRegistrationStatus && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500 mb-2">SMS Registration Status</dt>
                      {smsRegistrationStatus.status ? (
                        <dd className="mt-1">
                          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border">
                            {smsRegistrationStatus.status === 'approved' ? (
                              <>
                                <Check className="w-4 h-4 mr-1.5 text-green-600" />
                                <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded-full border-green-200">
                                  Approved
                                </span>
                              </>
                            ) : smsRegistrationStatus.status === 'rejected' ? (
                              <>
                                <X className="w-4 h-4 mr-1.5 text-red-600" />
                                <span className="text-red-700 bg-red-50 px-2 py-0.5 rounded-full border-red-200">
                                  Rejected
                                </span>
                              </>
                            ) : smsRegistrationStatus.status === 'in_review' ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1.5 text-yellow-600 animate-spin" />
                                <span className="text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded-full border-yellow-200">
                                  In Review
                                </span>
                              </>
                            ) : (
                              <>
                                <AlertTriangle className="w-4 h-4 mr-1.5 text-gray-600" />
                                <span className="text-gray-700 bg-gray-50 px-2 py-0.5 rounded-full border-gray-200">
                                  {smsRegistrationStatus.status.replace('_', ' ').toUpperCase()}
                                </span>
                              </>
                            )}
                          </div>
                          {smsRegistrationStatus.updatedAt && (
                            <p className="text-xs text-gray-500 mt-1">
                              Last updated:{' '}
                              {new Date(smsRegistrationStatus.updatedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          )}
                        </dd>
                      ) : (
                        <dd className="mt-1 text-sm text-gray-400 italic">
                          No SMS registration application submitted yet
                        </dd>
                      )}
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created At</dt>
                    <dd className="mt-1 text-sm text-gray-900 inline-flex items-center">
                      <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                      {new Date(tenant.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                    <dd className="mt-1 text-sm text-gray-900 inline-flex items-center">
                      <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                      {new Date(tenant.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </dd>
                  </div>
                </dl>

                <BrandConfigCard tenantName={tenant.name} branding={tenant.branding} />

                {/* Integration Status */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Integration Status</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Website Tracking */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Website Tracking</h5>
                      <div className="flex items-start space-x-3">
                        {tenant.websiteTrackingCompletedAt ? (
                          <>
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                <Check className="h-5 w-5 text-green-600" />
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Tracking Configured</p>
                              <p className="text-sm text-gray-500">
                                Completed on{' '}
                                {new Date(tenant.websiteTrackingCompletedAt).toLocaleDateString(
                                  'en-US',
                                  {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  },
                                )}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                                <X className="h-5 w-5 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Tracking Not Configured
                              </p>
                              <p className="text-sm text-gray-500">
                                Website tracking has not been set up yet
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Data Integration */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Data Integration</h5>
                      <div className="flex items-start space-x-3">
                        {tenant.dataIntegrationCompletedAt ? (
                          <>
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                <Check className="h-5 w-5 text-green-600" />
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Integration Complete</p>
                              <p className="text-sm text-gray-500">
                                Completed on{' '}
                                {new Date(tenant.dataIntegrationCompletedAt).toLocaleDateString(
                                  'en-US',
                                  {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  },
                                )}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                                <X className="h-5 w-5 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Integration Pending</p>
                              <p className="text-sm text-gray-500">
                                Data integration has not been completed yet
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* UI Defaults */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">UI Preferences</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-sm font-medium text-gray-700">Email Help Dialog</h5>
                        <p className="text-sm text-gray-500">Show email help dialog to users</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={tenant.uiDefaults?.emailHelpDialogShow ?? false}
                          onChange={e => handleUiDefaultChange('emailHelpDialogShow', e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-sm font-medium text-gray-700">Engagement Onboarding</h5>
                        <p className="text-sm text-gray-500">
                          Show engagement onboarding wizard to users
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={tenant.uiDefaults?.engageOnboardingShow ?? true}
                          onChange={e => handleUiDefaultChange('engageOnboardingShow', e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-sm font-medium text-gray-700">
                          Disable Acquire Learning Phase
                        </h5>
                        <p className="text-sm text-gray-500">
                          Hide the learning phase banner on the Measure Dashboard
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={tenant.uiDefaults?.acquireLearningPhaseDisabled ?? false}
                          onChange={e =>
                            handleUiDefaultChange('acquireLearningPhaseDisabled', e.target.checked)
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Resources by Schema */}
                <ResourcesBySchemaCard resources={resourceStats?.resources || []} />

                {!tenant.deletedAt && !isEditing && (
                  <div className="flex items-center space-x-2 mt-6 pt-6 border-t border-gray-200">
                    <Button variant="outline" onClick={() => setIsEditing(true)}>
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button variant="destructive" onClick={() => setShowDeactivateDialog(true)}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Deactivate
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'locations' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tenant Locations</h3>
            {isLoadingLocations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : !locations || locations.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No Locations</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This tenant has no locations configured yet.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {locations.map(location => (
                  <div key={location.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">
                          {location.name || 'Unnamed Location'}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">External ID: {location.externalId}</p>
                        {location.addressLine1 && (
                          <div className="mt-2 text-sm text-gray-700">
                            <p>{location.addressLine1}</p>
                            {location.addressLine2 && <p>{location.addressLine2}</p>}
                            {location.addressLine3 && <p>{location.addressLine3}</p>}
                            <p>
                              {[location.locality, location.district, location.postalCode]
                                .filter(Boolean)
                                .join(', ')}
                            </p>
                            {location.countryCode && <p>{location.countryCode}</p>}
                          </div>
                        )}
                        <div className="mt-3 space-y-1">
                          {location.phone && (
                            <p className="text-sm text-gray-600">Phone: {location.phone}</p>
                          )}
                          {location.email && (
                            <p className="text-sm text-gray-600">Email: {location.email}</p>
                          )}
                          {location.website && (
                            <p className="text-sm text-gray-600">
                              Website:{' '}
                              <a
                                href={location.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline">
                                {location.website}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewLocationResource(location)}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Users</h3>
            <PartnerUsersTable tenantId={id!} />
          </div>
        )}

        {activeTab === 'transactions' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Transactions</h3>
            <TransactionsTable tenantId={id!} />
          </div>
        )}

        {activeTab === 'sms' && <TenantSmsTab tenant={tenant} />}
      </div>

      {/* Location Resource Modal */}
      {showLocationResourceModal && selectedLocation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => {
              setShowLocationResourceModal(false);
              setSelectedLocation(null);
            }}
          />

          {/* Dialog */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-medium text-gray-900 font-nav">Location Details</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Location ID: {selectedLocation.externalId}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowLocationResourceModal(false);
                    setSelectedLocation(null);
                  }}
                  className="text-gray-400 hover:text-gray-500">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Loading State */}
              {!selectedLocationSchemaId && locationSchemas.length > 1 && (
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
                  </div>
                </div>
              )}

              {/* Content */}
              {selectedLocationSchemaId && (
                <div className="flex-1 overflow-y-auto p-6">
                  <PartnerResourceModal
                    tenantId={id!}
                    resource={{
                      externalId: selectedLocation.externalId,
                      displayName: selectedLocation.name || `Location ${selectedLocation.externalId}`,
                    }}
                    isOpen={true}
                    onClose={() => {
                      setShowLocationResourceModal(false);
                      setSelectedLocation(null);
                      setSelectedLocationSchemaId('');
                    }}
                    schemaDefinitionId={selectedLocationSchemaId}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Dialog */}
      {showDeactivateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Deactivate Tenant</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to deactivate <strong>{tenant.name}</strong>? The tenant will be
              hidden from your active tenant list, but can be reactivated at any time. No data will be
              permanently removed.
            </p>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowDeactivateDialog(false)}
                disabled={deactivateMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deactivateMutation.mutate()}
                disabled={deactivateMutation.isPending}>
                {deactivateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deactivating...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Deactivate
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
